import { Box } from '@apia/theme';
import { observer } from 'mobx-react-lite';
import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getLabel } from '@apia/util';
import { AutogrowTextarea, IconButton } from '@apia/components';
import { ChatMessage } from './ChatMessage';

const PENDING_PANEL_HISTORY_GAP = 6;
const VISIBLE_PENDING_MESSAGES = 3;

function labelText(code: string, fallback: string) {
  const text = getLabel(code).text;
  if (!text || text.startsWith('LBL_NOT_FOUND')) {
    return fallback;
  }
  return text;
}

function getMessageText(message: ChatMessage) {
  return typeof message.message === 'string' ? message.message : '';
}

function getMessagePreview(message: ChatMessage) {
  const text = getMessageText(message).trim();
  if (text) {
    return text;
  }

  if (message.attachments.length === 1) {
    return message.attachments[0].name;
  }

  if (message.attachments.length > 1) {
    return `${message.attachments.length} ${labelText(
      'btnUpload',
      'archivos',
    )}`;
  }

  return labelText('lblMessage', 'Mensaje');
}

function stopPropagation(ev: MouseEvent<HTMLElement>) {
  ev.preventDefault();
  ev.stopPropagation();
}

export const PendingMessagesPanel = observer(
  ({
    messages,
    onHeightChange,
    onRemoveMessage,
    onUpdateMessage,
  }: {
    messages: ChatMessage[];
    onHeightChange?: (height: number) => unknown;
    onRemoveMessage: (message: ChatMessage) => unknown;
    onUpdateMessage: (message: ChatMessage, value: string) => unknown;
  }) => {
    const stackRef = useRef<HTMLDivElement | null>(null);
    const [editingMessageId, setEditingMessageId] = useState<number | null>(
      null,
    );
    const [draft, setDraft] = useState('');
    const editingMessage =
      messages.find((message) => message.id === editingMessageId) ?? null;

    const updateHeight = useCallback(() => {
      const height = stackRef.current
        ? Math.ceil(
            stackRef.current.getBoundingClientRect().height +
              PENDING_PANEL_HISTORY_GAP,
          )
        : 0;
      onHeightChange?.(height);
    }, [onHeightChange]);

    useEffect(() => {
      updateHeight();
      const stack = stackRef.current;
      if (!stack) {
        return undefined;
      }

      const observer = new ResizeObserver(updateHeight);
      observer.observe(stack);

      return () => observer.disconnect();
    }, [messages.length, updateHeight]);

    useEffect(() => {
      if (editingMessageId === null) {
        return;
      }

      const message = messages.find(
        (current) => current.id === editingMessageId,
      );
      if (!message) {
        setEditingMessageId(null);
        setDraft('');
      }
    }, [editingMessageId, messages]);

    if (messages.length === 0) {
      return null;
    }

    const beginEdit = (message: ChatMessage) => {
      setEditingMessageId(message.id);
      setDraft(getMessageText(message));
    };

    const submitEdit = (ev: FormEvent) => {
      ev.preventDefault();
      if (editingMessage) {
        onUpdateMessage(editingMessage, draft);
      }
      setEditingMessageId(null);
      setDraft('');
    };

    const cancelEdit = () => {
      setEditingMessageId(null);
      setDraft('');
    };

    const renderMessage = (message: ChatMessage) => {
      const isEditing = editingMessageId === message.id;

      if (isEditing) {
        return (
          <Box
            as="form"
            className="pendingMessagesPanel__edit"
            key={message.id}
            onSubmit={submitEdit}
          >
            <AutogrowTextarea
              autoFocus
              className="pendingMessagesPanel__textarea"
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.code === 'Escape') {
                  ev.preventDefault();
                  cancelEdit();
                }
                if (
                  (ev.code === 'Enter' || ev.code === 'NumpadEnter') &&
                  !ev.shiftKey
                ) {
                  ev.preventDefault();
                  (ev.currentTarget.closest('form') as HTMLFormElement)
                    ?.requestSubmit();
                }
              }}
            />
            <Box className="pendingMessagesPanel__editActions">
              <IconButton
                className="pendingMessagesPanel__action"
                icon="Close"
                iconSize="Xs"
                title={getLabel('btnCan').text}
                variant="icon-only"
                onClick={cancelEdit}
              />
              <IconButton
                className="pendingMessagesPanel__action"
                icon="ArrowRight"
                iconSize="Xs"
                title={getLabel('btnCon').text}
                type="submit"
                variant="icon-only"
              />
            </Box>
          </Box>
        );
      }

      return (
        <Box
          as="button"
          className="pendingMessagesPanel__message"
          key={message.id}
          title={getMessagePreview(message)}
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            beginEdit(message);
          }}
        >
          <span className="pendingMessagesPanel__messageText">
            {getMessagePreview(message)}
          </span>
          <Box
            className="pendingMessagesPanel__actions"
            onClick={stopPropagation}
          >
            <IconButton
              className="pendingMessagesPanel__action"
              icon="Pencil"
              iconSize="Xs"
              title={getLabel('lblEdit').text}
              variant="icon-only"
              onClick={() => beginEdit(message)}
            />
            <IconButton
              className="pendingMessagesPanel__action"
              icon="Trash"
              iconSize="Xs"
              title={getLabel('btnDel').text}
              variant="icon-only"
              onClick={() => onRemoveMessage(message)}
            />
          </Box>
        </Box>
      );
    };

    const visibleMessages = messages.slice(0, VISIBLE_PENDING_MESSAGES);
    const overflowMessages = messages.slice(VISIBLE_PENDING_MESSAGES);

    return (
      <Box className="pendingMessagesPanel">
        <Box className="pendingMessagesPanel__stack" ref={stackRef}>
          {visibleMessages.map(renderMessage)}
          {overflowMessages.length > 0 && (
            <Box className="pendingMessagesPanel__overflow">
              <Box
                className="pendingMessagesPanel__overflowTrigger"
                tabIndex={0}
                title={`${overflowMessages.length} mensajes pendientes`}
              >
                ...
              </Box>
              <Box className="pendingMessagesPanel__overflowPopup">
                {overflowMessages.map(renderMessage)}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);
